import { FileFormatEnum } from '@components/cockpit/panels/notificationSearch/export/NotificationSearchExportDialog';
import { getFileNameFromHttpResponse } from '@utils/file.utils';

import {
  AlertCriteria,
  AlertLight,
  DroneTrajectories,
  NotificationCriteria,
  NotificationDetailRequest,
  NotificationDetails,
  NotificationDetailsBatchRequest,
  NotificationType,
} from '@/types/data/data.types';
import { DroneStatistics } from '@/types/data/statistic.types';
import { FileDirectoryDownloadResponseData } from '@/types/download.types';
import { SensorTypeEnum } from '@/types/sensor/sensors';

import { DATA_URL, dataApi } from './data.api';

export const notificationApi = dataApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query<NotificationType[], NotificationCriteria>({
      query: ({ site, ...rest }) => ({
        url: `${DATA_URL}/notifications`,
        params: {
          sites: [site],
          ...rest,
        },
      }),
    }),
    exportNotifications: builder.mutation<
      FileDirectoryDownloadResponseData,
      { exportedFormats: FileFormatEnum[]; criteria: NotificationCriteria }
    >({
      query: ({ exportedFormats, criteria }) => {
        const { site, ...rest } = criteria;

        return {
          url: `${DATA_URL}/notifications/export/${exportedFormats}`,
          method: 'GET',
          params: {
            sites: [site],
            ...rest,
          },
          responseHandler: async (response) => ({
            blob: await response.blob(),
            name: getFileNameFromHttpResponse(response, 'export-notifications.zip'),
            responseStatus: response.status,
          }),
        };
      },
    }),
    getNotificationIds: builder.query<string[], { criteria: NotificationCriteria; displayIds: boolean }>({
      query: ({ criteria, displayIds }) => ({
        url: `${DATA_URL}/notifications/${displayIds ? 'display-ids' : 'ids'}`,
        params: {
          ...criteria,
          sites: [criteria.site],
          unpaginated: true,
        },
      }),
    }),
    totalOfNotifications: builder.query<number, NotificationCriteria>({
      query: ({ site, ...rest }) => ({
        url: `${DATA_URL}/notifications/count`,
        params: {
          ...rest,
          sites: [site],
          unpaginated: true,
        },
      }),
    }),
    getDetail: builder.query<NotificationDetails, NotificationDetailRequest>({
      query: ({ id, withArchivedAlerts, withDoneProcedures }) => ({
        url: `${DATA_URL}/notifications/details/${id}`,
        params: {
          withArchivedAlerts,
          withDoneProcedures,
        },
      }),
      providesTags: (result) => {
        return result ? [{ type: 'Notification', id: result.notification.id }] : [];
      },
    }),
    getDroneStatistics: builder.query<DroneStatistics, NotificationCriteria>({
      query: ({ site, ...rest }) => ({
        url: `${DATA_URL}/notifications/drones/statistics`,
        params: {
          sites: [site],
          ...rest,
          unpaginated: true,
        },
      }),
    }),
    getDroneTrajectories: builder.mutation<DroneTrajectories[], NotificationDetailsBatchRequest>({
      query: ({ ids }) => ({
        url: `${DATA_URL}/notifications/drones/trajectories`,
        method: 'POST',
        body: ids,
      }),
    }),
    startProcedure: builder.mutation<NotificationType, string>({
      query: (notificationId) => ({
        url: `${DATA_URL}/notifications/${notificationId}/start-procedure`,
        method: 'PATCH',
      }),
      invalidatesTags: (_, __, notificationId) => [{ type: 'Notification', id: notificationId }],
    }),
    muteNotification: builder.mutation<NotificationType, string>({
      query: (notificationId) => ({
        url: `${DATA_URL}/notifications/${notificationId}/mute`,
        method: 'PATCH',
      }),
      invalidatesTags: (_, __, notificationId) => [{ type: 'Notification', id: notificationId }],
    }),
    archiveAll: builder.mutation<void, boolean>({
      query: (includeInProgress) => ({
        url: `${DATA_URL}/notifications/archive-all`,
        params: {
          includeInProgress,
        },
        method: 'POST',
      }),
    }),
    getSensorAvailabilityAndAlerts: builder.query<
      { availability: number; alertLights: AlertLight[] },
      { criteria: AlertCriteria; sensorType: SensorTypeEnum }
    >({
      query: ({ criteria, sensorType }) => ({
        url: `${DATA_URL}/notifications/sensor-availability/${sensorType}`,
        params: {
          ...criteria,
          sites: [criteria.site],
          unpaginated: true,
        },
      }),
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetDroneStatisticsQuery,
  useLazyGetNotificationsQuery,
  useExportNotificationsMutation,
  useLazyGetNotificationIdsQuery,
  useTotalOfNotificationsQuery,
  useLazyGetDetailQuery,
  useGetDetailQuery,
  useGetDroneTrajectoriesMutation,
  useStartProcedureMutation,
  useMuteNotificationMutation,
  useArchiveAllMutation,
  useGetSensorAvailabilityAndAlertsQuery,
} = notificationApi;
